import React, { useState, useEffect } from 'react';
import { X, Clock, Globe, MapPin, Monitor, Smartphone, Tablet, CreditCard, CheckCircle, User } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface SessionEvent {
  id: number;
  visitorId?: string;
  userId?: number;
  eventType: string;
  url: string;
  referrer?: string;
  ipAddress?: string;
  userAgent?: string;
  browserName?: string;
  browserVersion?: string;
  osName?: string;
  osVersion?: string;
  deviceType?: string;
  deviceInfo?: any;
  queryParams?: any;
  source?: string;
  medium?: string;
  campaign?: string;
  screenWidth?: number;
  screenHeight?: number;
  language?: string;
  createdAt: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  userTelegramUsername?: string;
  isEmailVerified?: boolean;
  userRegistrationDate?: string;
  userLastLogin?: string;
}

interface SessionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitorId?: string;
  userId?: number;
  sessionInfo?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    firstVisit?: string;
    lastActivity?: string;
  };
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  visitorId, 
  userId,
  sessionInfo 
}) => {
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && (visitorId || userId)) {
      fetchSessionDetails();
    }
  }, [isOpen, visitorId, userId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const id = userId || visitorId;
      if (!id) {
        toast.error('Не указан идентификатор сессии');
        return;
      }
      
      const response = await api.get(`/admin/sessions/${id}/details`, {
        params: userId ? { type: 'user' } : { type: 'visitor' }
      });

      if (response.data.success) {
        setEvents(response.data.events || []);
      } else {
        toast.error('Ошибка загрузки истории сессии');
        setEvents([]);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки истории сессии:', error);
      toast.error(error.response?.data?.message || 'Ошибка загрузки истории сессии');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getDeviceIcon = (deviceType?: string) => {
    if (!deviceType) return <Monitor className="h-4 w-4" />;
    switch (deviceType.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'tablet':
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const isPurchaseEvent = (event: SessionEvent) => {
    return event.eventType === 'purchase' || 
           event.eventType === 'subscription_purchased' ||
           event.url.includes('/payment/success') ||
           event.url.includes('/subscription/success');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">История сессии</h2>
            {sessionInfo && (
              <div className="mt-2 text-sm text-gray-600">
                {sessionInfo.email ? (
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    {sessionInfo.firstName} {sessionInfo.lastName} ({sessionInfo.email})
                  </div>
                ) : (
                  <div>Гость (visitor_id: {visitorId?.substring(0, 8)}...)</div>
                )}
                <div className="mt-1 flex items-center text-xs text-gray-500">
                  <Clock className="h-3 w-3 mr-1" />
                  Первое посещение: {sessionInfo.firstVisit ? formatDate(sessionInfo.firstVisit) : '—'}
                  <span className="mx-2">•</span>
                  Последняя активность: {sessionInfo.lastActivity ? formatDate(sessionInfo.lastActivity) : '—'}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Нет данных о посещениях
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event, index) => (
                <div
                  key={event.id || index}
                  className={`border rounded-lg p-4 ${
                    isPurchaseEvent(event)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {isPurchaseEvent(event) && (
                          <CreditCard className="h-5 w-5 text-green-600" />
                        )}
                        <span className={`font-medium ${
                          isPurchaseEvent(event) ? 'text-green-800' : 'text-gray-900'
                        }`}>
                          {isPurchaseEvent(event) ? 'Покупка подписки' : 'Просмотр страницы'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(event.createdAt)}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center text-gray-700">
                          <Globe className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-mono">{event.url}</span>
                        </div>
                        
                        {event.referrer && (
                          <div className="flex items-center text-gray-600 text-xs">
                            <span className="mr-2">Источник:</span>
                            <span>{event.referrer}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            {event.ipAddress}
                          </div>
                          <div className="flex items-center">
                            {getDeviceIcon(event.deviceType)}
                            <span className="ml-1 capitalize">{event.deviceType || 'desktop'}</span>
                          </div>
                          {event.browserName && (
                            <div>
                              {event.browserName} {event.browserVersion}
                            </div>
                          )}
                          {event.osName && (
                            <div>
                              {event.osName} {event.osVersion}
                            </div>
                          )}
                        </div>
                        
                        {event.source && (
                          <div className="mt-2 text-xs">
                            <span className="text-gray-600">Источник трафика: </span>
                            <span className="font-medium">{event.source}</span>
                            {event.medium && (
                              <>
                                <span className="text-gray-600"> / </span>
                                <span className="font-medium">{event.medium}</span>
                              </>
                            )}
                          </div>
                        )}
                        
                        {event.queryParams && Object.keys(event.queryParams).length > 0 && (
                          <div className="mt-2 text-xs">
                            <span className="text-gray-600">Параметры URL:</span>
                            <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                              {JSON.stringify(event.queryParams, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;




